<template>
  <div>
    <v-btn
      rounded="pill"
      class="annotation-button"
      :class="{ active: annotationMode && annotationStore.isGlobal }"
      @click="toggleAnnotationMode"
    >
      <v-icon class="annotation-button-icon">fa-light fa-paintbrush-pencil</v-icon>
      <span class="d-none d-md-inline">{{ annotationMode ? "Izslēgt" : "Anotēt" }}</span>
    </v-btn>

    <Teleport to="body">
      <div v-if="annotationMode && annotationStore.isGlobal" class="toolbar-placeholder">
        <AnnotationToolbar @drag="updateCanvasClipPath" />
      </div>

      <div
        v-if="(colorPickerOpen || sizeSliderOpen) && annotationMode && annotationStore.isGlobal"
        class="annotation-picker-overlay"
        @mousedown.prevent="colorPickerOpen = false; sizeSliderOpen = false"
        @touchstart.prevent="colorPickerOpen = false; sizeSliderOpen = false"
      />

      <div
        v-if="annotationMode && annotationStore.isGlobal"
        class="annotation-canvas-wrapper"
        :class="{ 'eraser-mode': isErasing, 'rectangle-mode': isDrawingFilledRectangle || isDrawingInverseFilledRectangle, 'over-button': isOverButton }"
        @mousedown="handleWrapperMouseDown"
        @touchstart="handleWrapperMouseDown"
        @mousemove="handleWrapperMouseMove"
      >
        <canvas
          ref="canvas"
          class="annotation-canvas"
          :class="{ 'eraser-cursor': isErasing, 'rectangle-cursor': isDrawingFilledRectangle || isDrawingInverseFilledRectangle, 'drawing': isDrawing }"
          @mousemove="draw"
          @mouseup="stopDrawing"
          @mouseleave="stopDrawing"
          @touchmove="draw"
          @touchend="stopDrawing"
        ></canvas>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue"

import AnnotationToolbar from "~/components/image/AnnotationToolbar.vue"
import { useImageAnnotation } from "~/composables/useImageAnnotation"
import { useAnnotationStore } from "~/stores/annotationStore"

export default defineComponent({
  components: {
    AnnotationToolbar,
  },
  setup() {
    const annotationStore = useAnnotationStore()
    const annotation = useImageAnnotation()

    return {
      annotationStore,
      ...annotation,
      isDragging: computed(() => annotationStore.isDragging)
    }
  },

  data() {
    return {
      isOverButton: false,
      isOpen: false,
      scrollPosition: 0,
    }
  },

  watch: {
    isDragging(newVal) {
      if (!newVal) {
        this.enableCanvasWrapper()
      }
    },
    annotationMode(newVal) {
      if (newVal) {
        this.scrollPosition = window.scrollY
        document.body.style.top = `-${this.scrollPosition}px`
        document.body.classList.add("global-annotation-open")
      } else {
        document.body.classList.remove("global-annotation-open")
        document.body.style.top = ""
        window.scrollTo(0, this.scrollPosition)
      }
    },

    colorPickerOpen(newVal: boolean) {
      this.$nextTick(() => {
        const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement | null
        if (wrapper) {
          wrapper.style.pointerEvents = newVal ? "none" : "auto"
        }
      })
    },

    sizeSliderOpen(newVal: boolean) {
      this.$nextTick(() => {
        const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement | null
        if (wrapper) {
          wrapper.style.pointerEvents = newVal ? "none" : "auto"
        }
      })
    },
  },

  mounted() {
    window.addEventListener("blur", this.handleWindowBlur)
  },

  unmounted() {
    document.body.classList.remove("global-annotation-open")
    document.body.style.top = ""
    window.removeEventListener("blur", this.handleWindowBlur)
  },

  methods: {
    toggleAnnotationMode() {
      this.annotationStore.isGlobal = !this.annotationMode
      this.annotationStore.toggleAnnotationMode()

      if (this.annotationMode) {
        this.handleInitCanvas()
      }
    },

    handleInitCanvas() {
      if (!this.annotationMode) {
        return
      }

      this.$nextTick(() => {
        const canvasElement = Array.isArray(this.$refs.canvas) ? this.$refs.canvas[0] : this.$refs.canvas

        if (!canvasElement) {
          return
        }

        canvasElement.width = window.innerWidth
        canvasElement.height = window.innerHeight

        const annotation = useImageAnnotation()
        annotation.canvas.value = canvasElement
        annotation.ctx.value = canvasElement.getContext("2d")

        if (annotation.ctx.value) {
          annotation.ctx.value.lineCap = "round"
          annotation.ctx.value.lineJoin = "round"
        }

        this.updateCanvasClipPath()
        this.enableCanvasWrapper()
      })
    },

    updateCanvasClipPath() {
      const toolbar = document.querySelector(".annotation-toolbar") as HTMLElement
      const canvas = document.querySelector(".annotation-canvas") as HTMLElement
      const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement

      if (!canvas || !toolbar) {
        return
      }

      const toolbarRect = toolbar.getBoundingClientRect()
      const r = 25 // radius

      const getRoundedRectPath = (rect: DOMRect, radius: number) => {
        const { left: x, top: y, width: w, height: h } = rect
        const r = Math.min(radius, w / 2, h / 2)
        // Counter-clockwise path for nonzero fill-rule holes.
        // Uses arc commands (A) instead of quadratic beziers (Q) to produce exact
        // circular arcs that match CSS border-radius precisely.
        return `M ${x + r} ${y} A ${r} ${r} 0 0 0 ${x} ${y + r} L ${x} ${y + h - r} A ${r} ${r} 0 0 0 ${x + r} ${y + h} L ${x + w - r} ${y + h} A ${r} ${r} 0 0 0 ${x + w} ${y + h - r} L ${x + w} ${y + r} A ${r} ${r} 0 0 0 ${x + w - r} ${y} Z`
      }

      // Start building the path - outer rectangle covers entire canvas
      let pathData = `M 0 0 L 100000 0 L 100000 100000 L 0 100000 Z`

      // Add rounded hole for toolbar
      pathData += ` ${getRoundedRectPath(toolbarRect, r)}`

      // Find the main annotation button and add a rounded hole for it as well
      const mainBtn = document.querySelector(".annotation-button") as HTMLElement
      if (mainBtn) {
        pathData += ` ${getRoundedRectPath(mainBtn.getBoundingClientRect(), r)}`
      }

      const polygon = `path('${pathData}')`
      canvas.style.clipPath = polygon
      if (wrapper) {
        wrapper.style.clipPath = polygon
      }
    },

    enableCanvasWrapper() {
      this.$nextTick(() => {
        const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement
        if (wrapper) {
          wrapper.style.pointerEvents = "auto"
          wrapper.style.zIndex = "2410"
        }
      })
    },

    handleWrapperMouseMove(e: MouseEvent) {
      const wrapper = e.currentTarget as HTMLElement
      wrapper.style.pointerEvents = "none"

      const elementBelow = document.elementFromPoint(e.clientX, e.clientY)
      const isOverInteractive = !!(elementBelow && elementBelow.closest(".annotation-button, .close-btn, .annotation-toolbar, .v-color-picker, .slider-menu-content"))
      this.isOverButton = isOverInteractive

      // Always restore pointer-events to auto so we can continue receiving events
      wrapper.style.pointerEvents = "auto"
    },

    // For cases when we have IFRAME (PDF inline) and user pick new color / change size.
    // When clicking on IFRAME, we close color picker/size slider.
    handleWindowBlur() {
      if (!this.annotationMode || !this.annotationStore.isGlobal) {
        return
      }

      requestAnimationFrame(() => {
        if (document.activeElement?.tagName === "IFRAME") {
          this.colorPickerOpen = false
          this.sizeSliderOpen = false
        }
      })
    },

    handleWrapperMouseDown(e: MouseEvent | TouchEvent) {
      const wrapper = e.currentTarget as HTMLElement
      wrapper.style.pointerEvents = "none"

      let clientX: number, clientY: number
      if ("touches" in e && e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX
        clientY = e.touches[0].clientY
      } else {
        clientX = (e as MouseEvent).clientX
        clientY = (e as MouseEvent).clientY
      }

      const elementBelow = document.elementFromPoint(clientX, clientY)

      const interactiveElement = elementBelow && elementBelow.closest(".annotation-button, .close-btn, .annotation-toolbar, .v-color-picker, .slider-menu-content")

      if (interactiveElement) {
        const draggableElement = elementBelow.closest(".drag-btn, .v-slider, .v-slider-thumb")
        const clickableElement = elementBelow.closest("button, .v-color-picker__swatch")
        const targetElement = clickableElement || elementBelow

        // For draggable elements, dispatch only the down events and keep wrapper transparent
        if (draggableElement) {
          const pointerId = (e as PointerEvent).pointerId || 0
          const pointerdownEvent = new PointerEvent("pointerdown", {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX,
            clientY,
            pointerId
          })
          targetElement.dispatchEvent(pointerdownEvent)

          const mousedownEvent = new MouseEvent("mousedown", {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX,
            clientY
          })
          targetElement.dispatchEvent(mousedownEvent)

          e.preventDefault()
          e.stopPropagation()
          return
        }

        const mousedownEvent = new MouseEvent("mousedown", {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX,
          clientY
        })
        targetElement.dispatchEvent(mousedownEvent)

        const mouseupEvent = new MouseEvent("mouseup", {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX,
          clientY
        })
        targetElement.dispatchEvent(mouseupEvent)

        const clickEvent = new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX,
          clientY
        })
        targetElement.dispatchEvent(clickEvent)

        e.preventDefault()
        e.stopPropagation()
        return
      }

      this.colorPickerOpen = false
      this.sizeSliderOpen = false

      wrapper.style.pointerEvents = "auto"
      this.startDrawing(e)
    },
  },
})
</script>

<style lang="sass">
@use "~/assets/css/vuetify/settings" as *

.annotation-button
  text-transform: none
  letter-spacing: 0.1px
  font-weight: 400
  font-size: 16px
  line-height: 20px
  min-width: 0

  &.active
    background-color: #FFF1AE

body.global-annotation-open
  position: fixed
  width: 100%

.annotation-button-icon
  margin-right: 8px

.toolbar-placeholder
  position: fixed
  bottom: 50px
  right: 120px
  z-index: 2600
  display: flex
  justify-content: flex-end
  pointer-events: none

.annotation-picker-overlay
  position: fixed
  top: 0
  left: 0
  width: 100vw
  height: 100vh
  z-index: 2550
  cursor: default

.annotation-canvas-wrapper
  position: fixed
  top: 0
  left: 0
  width: 100vw
  height: 100vh
  pointer-events: auto
  z-index: 2500
  cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="%23000" d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25z"/></svg>') 0 24, auto

  &.over-button
    cursor: pointer

  &.eraser-mode
    cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="%23000" d="M16.24 3.56l4.95 4.94c.78.79.78 2.05 0 2.84L12 20.53a4.008 4.008 0 0 1-5.66 0L2.81 17c-.78-.79-.78-2.05 0-2.84l10.6-10.6c.79-.78 2.05-.78 2.83 0M4.22 15.58l3.54 3.53c.78.79 2.04.79 2.83 0l3.53-3.53l-4.95-4.95l-4.95 4.95z"/></svg>') 12 12, auto

    &.over-button
      cursor: pointer

  &.rectangle-mode
    cursor: crosshair

    &.over-button
      cursor: pointer

.annotation-canvas
  width: 100%
  height: 100%
  pointer-events: none
  cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="%23000" d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25z"/></svg>') 0 24, auto
  touch-action: none

  &.drawing
    pointer-events: auto

  &.eraser-cursor
    cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="%23000" d="M16.24 3.56l4.95 4.94c.78.79.78 2.05 0 2.84L12 20.53a4.008 4.008 0 0 1-5.66 0L2.81 17c-.78-.79-.78-2.05 0-2.84l10.6-10.6c.79-.78 2.05-.78 2.83 0M4.22 15.58l3.54 3.53c.78.79 2.04.79 2.83 0l3.53-3.53l-4.95-4.95l-4.95 4.95z"/></svg>') 12 12, auto

  &.rectangle-cursor
    cursor: crosshair
</style>
